import { observable, runInAction, computed } from "mobx"

import { fetchGetApplications, Application } from "~/api/applications/get"
import { fetchPostApplication } from "~/api/applications/post"
import { fetchPostCourseApplicationByGuest } from "~/api/course-application-by-guest/post"
import { fetchPostCourseApplication } from "~/api/course-application/post"
import { RecruitmentTypeBySnakeCase } from "~/consts"
import { TmpUserInfo } from "~/entities/User"

export default class ApplicationStore {
  @observable applications: Array<Application> = []
  @observable newGradJobIds: Array<number> = []
  @observable shortInternshipIds: Array<number> = []
  @observable longInternshipIds: Array<number> = []
  @observable eventIds: Array<number> = []
  @observable eventScheduleIds: Array<number> = []

  fetch = async () => {
    const applications = await fetchGetApplications()
    runInAction(() => {
      this.applications = applications
      this.newGradJobIds = this._devideByCategory(applications, "new_grad_job")
      this.shortInternshipIds = this._devideByCategory(applications, "short_internship")
      this.longInternshipIds = this._devideByCategory(applications, "long_internship")
      this.eventIds = this._devideByCategory(applications, "event")
      this.eventScheduleIds = Array.isArray(applications)
        ? applications.flatMap((application) => {
            if (application.category_name !== "event") {
              return []
            } else {
              return [application.pivot.event_schedule_id]
            }
          })
        : []
    })
  }

  apply = async (category: keyof typeof RecruitmentTypeBySnakeCase, id: number, scheduleId?: number) => {
    const convertedCategory = RecruitmentTypeBySnakeCase[category]
    await fetchPostApplication({
      id: id,
      category_name: convertedCategory,
      short_internship_schedule_id: category === "shortInternship" ? scheduleId : undefined,
      event_schedule_id: category === "event" ? scheduleId : undefined,
    })

    runInAction(() => {
      switch (convertedCategory) {
        case "new_grad_job": {
          this.newGradJobIds.push(id)
          break
        }
        case "short_internship": {
          this.shortInternshipIds.push(id)
          break
        }
        case "long_internship": {
          this.longInternshipIds.push(id)
          break
        }
        case "event": {
          this.eventIds.push(id)
          this.eventScheduleIds.push(scheduleId!)
          break
        }
      }
    })
  }

  courseConsultationApply = async (courseId: number, preferredDateTimes: string[], channel?: string) => {
    const data = await fetchPostCourseApplication({
      course_id: courseId,
      consultation: true,
      preferred_date_times: preferredDateTimes,
      channel: channel,
    })
  }

  courseConsultationApplyByGuest = async (
    courseId: number,
    tmpUserInfo: TmpUserInfo,
    preferredDateTimes: string[],
    channel?: string
  ) => {
    const data = await fetchPostCourseApplicationByGuest({
      course_id: courseId,
      consultation: true,
      name: tmpUserInfo.name,
      phone_number: tmpUserInfo.phoneNumber,
      email: tmpUserInfo.email,
      preferred_date_times: preferredDateTimes,
      channel: channel,
    })
  }

  isApplied = (id: number, category: keyof typeof RecruitmentTypeBySnakeCase) => {
    const convertedCategory = RecruitmentTypeBySnakeCase[category]
    switch (convertedCategory) {
      case "new_grad_job":
        return this.newGradJobIds.includes(id)
      case "short_internship":
        return this.shortInternshipIds.includes(id)
      case "long_internship":
        return this.longInternshipIds.includes(id)
      case "event":
        return this.eventIds.includes(id)
    }
  }

  _devideByCategory = (applications: Application[], category: Application["category_name"]) => {
    const _applications = Array.isArray(applications) ? applications : []
    return _applications.flatMap((application) => {
      if (application.category_name === category && !application.pivot?.is_scout) {
        return [application.id]
      } else {
        return []
      }
    })
  }
}
